import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { approvalsApi } from './api'
import type { ApprovalDelegationPayload, ApprovalPolicyPayload } from './types'

export function useApprovalPolicies(params?: { resource_type?: string; active_only?: boolean }) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'approvals', 'policies', params || {}),
    queryFn: () => approvalsApi.listPolicies(params),
  })
}

export function useApprovalPolicy(id: number | undefined) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'approvals', 'policy', id || 0),
    queryFn: () => approvalsApi.getPolicy(id as number),
    enabled: !!id,
  })
}

export function useApprovalCatalog() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'approvals', 'catalog'),
    queryFn: () => approvalsApi.catalog(),
    staleTime: 5 * 60 * 1000,
  })
}

function invalidatePolicies(qc: ReturnType<typeof useQueryClient>, tenantId: string | null, id?: number) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals', 'policies') })
  if (id !== undefined) qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals', 'policy', id) })
}

export function useCreateApprovalPolicy() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: ApprovalPolicyPayload) => approvalsApi.createPolicy(body),
    onSuccess: (res) => invalidatePolicies(qc, tenantId, res.policy.id),
  })
}

export function useUpdateApprovalPolicy() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<ApprovalPolicyPayload> }) =>
      approvalsApi.updatePolicy(id, body),
    onSuccess: (res) => invalidatePolicies(qc, tenantId, res.policy.id),
  })
}

export function useDeleteApprovalPolicy() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => approvalsApi.deletePolicy(id),
    onSuccess: () => invalidatePolicies(qc, tenantId),
  })
}

// ---------- Instances + inbox ----------

export function useApprovalInbox(params?: { limit?: number; offset?: number }) {
  const tenantId = useTenant()
  const limit = params?.limit ?? 50
  const offset = params?.offset ?? 0
  return useQuery({
    queryKey: tenantKey(tenantId, 'approvals', 'inbox', { limit, offset }),
    queryFn: () => approvalsApi.inbox({ limit, offset }),
  })
}

export function useApprovalInboxSummary() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'approvals', 'inbox', 'summary'),
    queryFn: () => approvalsApi.inboxSummary(),
    staleTime: 60 * 1000,
  })
}

export function useApprovalInstances(params?: { status?: string; resource_type?: string }) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'approvals', 'instances', params || {}),
    queryFn: () => approvalsApi.listInstances(params),
  })
}

export function useApprovalInstance(id: number | undefined) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'approvals', 'instance', id || 0),
    queryFn: () => approvalsApi.getInstance(id as number),
    enabled: !!id,
  })
}

// ---------- Delegations (Phase 3c) ----------

export function useApprovalDelegations() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'approvals', 'delegations'),
    queryFn: () => approvalsApi.listDelegations(),
  })
}

export function useCreateApprovalDelegation() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: ApprovalDelegationPayload) => approvalsApi.createDelegation(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals', 'delegations') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals', 'inbox') })
    },
  })
}

export function useRevokeApprovalDelegation() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => approvalsApi.revokeDelegation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals', 'delegations') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals', 'inbox') })
    },
  })
}

export function useActOnApproval() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, action, comment, metadata }: {
      id: number
      action: 'approve' | 'reject' | 'comment' | 'cancel'
      comment?: string
      metadata?: Record<string, unknown>
    }) => approvalsApi.act(id, { action, comment, metadata }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals', 'instance', res.instance.id) })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals', 'instances') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals', 'inbox') })
    },
  })
}
